'use client'

import { useEffect, useMemo, useState } from 'react'
import { LuDownload, LuHighlighter, LuTrash2 } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import { toast } from '@/core/lib/toast'

import { ShareIcon } from '@/features/veluvit/components/ui/action-icons'
import {
  canShareFiles,
  downloadBlob,
  excerptFileName,
  renderExcerptBlob,
  shareBlob,
} from '@/features/veluvit/components/reader/excerpt-canvas'
import ExcerptPreview from '@/features/veluvit/components/reader/excerpt-preview'
import PageHeader from '@/features/veluvit/components/shell/page-header'
import {
  deleteExcerpt,
  listExcerpts,
} from '@/features/veluvit/services/excerpt.service'

import type { ExcerptCard } from '@/features/veluvit/components/reader/excerpt-canvas'
import type { Excerpt } from '@/features/veluvit/services/excerpt.service'

/** 카드 하나 — 저장한 글 · 모양으로 다시 그린다(이미지 파일을 보관하지 않는다) */
function ExcerptTile({
  excerpt,
  shareable,
  onDelete,
}: {
  excerpt: Excerpt
  shareable: boolean
  onDelete: () => void
}) {
  const card: ExcerptCard = useMemo(
    () => ({
      paragraphs: excerpt.paragraphs,
      style: excerpt.style,
      charName: excerpt.charName,
      author: excerpt.author,
      userName: excerpt.userName,
    }),
    [excerpt]
  )
  const [busy, setBusy] = useState(false)
  const run = async (share: boolean) => {
    setBusy(true)
    try {
      const blob = await renderExcerptBlob(card)
      const name = excerptFileName(excerpt.charName)
      if (share) await shareBlob(blob, name, `${excerpt.charName} — veluvit`)
      else downloadBlob(blob, name)
    } catch (e) {
      toast.show(e instanceof Error ? e.message : '이미지를 만들지 못했어요')
    } finally {
      setBusy(false)
    }
  }
  return (
    <figure className="m-0 flex min-w-0 flex-col">
      <div className="grid aspect-[3/4] place-items-center rounded-panel bg-foreground/[0.04] p-3 shadow-[inset_0_0_0_1px_rgb(255_255_255/0.05)]">
        <ExcerptPreview card={card} />
      </div>
      <figcaption className="mt-2.5 flex min-w-0 items-center gap-1">
        <span className="min-w-0 flex-1">
          <b className="block truncate text-c15 leading-[1.3] font-bold text-t1">
            {excerpt.charName}
          </b>
          <span className="block text-c12 text-t3 tabular-nums">
            {new Date(excerpt.createdAt).toLocaleDateString('ko-KR')}
          </span>
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          shape="pill"
          aria-label="이미지 저장"
          title="이미지 저장"
          disabled={busy}
          onClick={() => void run(false)}
        >
          <LuDownload aria-hidden="true" className="size-4 text-t2" />
        </Button>
        {shareable && (
          <Button
            variant="ghost"
            size="icon-sm"
            shape="pill"
            aria-label="공유"
            title="공유"
            disabled={busy}
            onClick={() => void run(true)}
          >
            <ShareIcon aria-hidden="true" className="size-4 text-t2" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          shape="pill"
          aria-label="발췌 지우기"
          title="지우기"
          disabled={busy}
          onClick={onDelete}
        >
          <LuTrash2 aria-hidden="true" className="size-4 text-t3" />
        </Button>
      </figcaption>
    </figure>
  )
}

/** 내 발췌함 — 대화에서 만든 문장 카드 (flow-renew ⑤) */
export default function ExcerptsScreen() {
  const [items, setItems] = useState<Excerpt[] | null>(null)
  const [error, setError] = useState('')
  const shareable = useMemo(
    () => (typeof window === 'undefined' ? false : canShareFiles()),
    []
  )

  useEffect(() => {
    let alive = true
    listExcerpts()
      .then((list) => alive && setItems(list))
      .catch((e: Error) => alive && setError(e.message))
    return () => {
      alive = false
    }
  }, [])

  const remove = async (id: string) => {
    try {
      await deleteExcerpt(id)
      setItems((prev) => prev?.filter((x) => x.id !== id) ?? null)
      toast.show('발췌를 지웠어요', { duration: 1400 })
    } catch (e) {
      toast.show(e instanceof Error ? e.message : '지우지 못했어요')
    }
  }

  return (
    <>
      <PageHeader back title="내 발췌함" />
      <section className="mx-auto w-full max-w-[1100px] px-4 pt-5 pb-10 over6:px-5 over6:pt-6">
        <p className="text-c13 leading-[1.6] text-t3 over10:text-c14">
          이 기기에 보관한 문장 카드예요. 다시 저장하거나 공유할 수 있어요.
        </p>
        {error ? (
          <p role="alert" className="mt-6 text-c13 text-error">
            {error}
          </p>
        ) : items === null ? (
          <p className="mt-6 text-c13 text-t3">불러오고 있어요…</p>
        ) : items.length === 0 ? (
          <div className="mt-14 flex flex-col items-center gap-3 text-center">
            <LuHighlighter aria-hidden="true" className="size-8 text-t4" />
            <p className="text-c15 font-semibold text-t1">
              아직 발췌한 문장이 없어요
            </p>
            <p className="text-c13 leading-[1.6] text-t3">
              대화에서 캐릭터 답장 아래 ‘발췌’를 누르거나, 소설 모드에서 문장을
              골라 보세요.
            </p>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-6 over6:grid-cols-3 over10:grid-cols-4 over10:gap-x-5">
            {items.map((x) => (
              <ExcerptTile
                key={x.id}
                excerpt={x}
                shareable={shareable}
                onDelete={() => void remove(x.id)}
              />
            ))}
          </div>
        )}
      </section>
    </>
  )
}
